import { PayloadAction } from "@reduxjs/toolkit";
import { createSearchParams } from "react-router-dom";
import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { AppState } from "../../../store";
import fetchAPI, {
  ApiResponse,
  HttpMethod,
  Pagination,
} from "../../../utils/api";
import { showSuccess } from "../../toaster/store/slice";
import companyPageSlice, { Company } from "./slice";

type CompanyListResponse = ApiResponse<{ data: Company[]; meta: Pagination }>;
type CompanyResponse = ApiResponse<{ data: Company[] }>;
type CompanyDeleteResponse = ApiResponse<{ success: true }>;

function* companyPageSaga() {
  yield takeLatest(companyPageSlice.actions.setSearch, handleDebounce);
  yield takeLatest(companyPageSlice.actions.loadMore, handleLoadMore);
  yield takeLatest(companyPageSlice.actions.create, handleCreate);
  yield takeLatest(companyPageSlice.actions.update, handleUpdate);
  yield takeLatest(companyPageSlice.actions.remove, handleRemove);
}

function* handleDebounce() {
  yield delay(300);

  yield call(handleFetch);
}

function* handleFetch() {
  const search: string = yield select(
    (state: AppState) => state.companyPage.search
  );
  const meta: Pagination | null = yield select(
    (state: AppState) => state.companyPage.meta
  );
  const page = meta && meta.current_page ? meta.current_page : 1;

  const params = createSearchParams({
    page: page.toString(),
    per_page: "10",
    search,
  }).toString();

  const response: CompanyListResponse = yield call(
    fetchAPI,
    `/companies?${params}`
  );

  if (response && response.data && response.meta) {
    yield put(companyPageSlice.actions.setData(response));
  }

  yield put(companyPageSlice.actions.setIsLoading(false));
}

function* handleLoadMore() {
  const meta: Pagination | null = yield select(
    (state: AppState) => state.companyPage.meta
  );
  const page = meta && meta.current_page ? meta.current_page : 1;

  const params = createSearchParams({
    page: page.toString(),
    per_page: "10",
  }).toString();

  const response: CompanyListResponse = yield call(
    fetchAPI,
    `/companies?${params}`
  );

  if (response && response.data && response.meta) {
    yield put(companyPageSlice.actions.addData(response));
  }

  yield put(companyPageSlice.actions.setIsLoading(false));
}

function* handleCreate({ payload: company }: PayloadAction<Company>) {
  const response: CompanyResponse = yield call(fetchAPI, `/companies`, {
    method: HttpMethod.POST,
    body: JSON.stringify(company),
  });

  if (response && response.data) {
    yield put(companyPageSlice.actions.setEditableIndex(undefined));
    yield put(companyPageSlice.actions.setSearch(""));
    yield put(showSuccess("Company created."));
  }

  yield put(companyPageSlice.actions.setIsCreating(false));
}

function* handleUpdate({ payload: company }: PayloadAction<Company>) {
  const response: CompanyResponse = yield call(
    fetchAPI,
    `/companies/${company.id}`,
    {
      method: HttpMethod.PUT,
      body: JSON.stringify(company),
    }
  );

  if (response && response.data) {
    yield put(companyPageSlice.actions.setEditableIndex(undefined));
    yield put(companyPageSlice.actions.updateCompany(response.data));
    yield put(showSuccess("Company updated."));
  }

  yield put(companyPageSlice.actions.setIsUpdating(false));
}

function* handleRemove({ payload: company }: PayloadAction<Company>) {
  const response: CompanyDeleteResponse = yield call(
    fetchAPI,
    `/companies/${company.id}`,
    {
      method: HttpMethod.DELETE,
    }
  );

  if (response && response.success) {
    yield put(companyPageSlice.actions.setEditableIndex(undefined));
    yield put(companyPageSlice.actions.setSearch(""));
    yield put(showSuccess("Company removed."));
  }

  yield put(companyPageSlice.actions.setIsRemoving(false));
}

export default companyPageSaga;
